/**
 * The small pulsing "Coming Soon" tag on More Features.
 *
 * It fades rather than blinks. A hard blink is the thing WCAG 2.2.2 is about,
 * and it is unpleasant to sit next to; a slow fade says "not yet" just as
 * clearly. The trough is 0.55 rather than something nearer zero so the label
 * stays readable at every point in the cycle — a tag you cannot read for half
 * of each second is decoration, not information.
 *
 * The animation stops entirely when the device asks for reduced motion, and
 * the tag is hidden from assistive technology because the control it sits on
 * already carries "More features are coming soon" as its hint — announcing it
 * twice would be worse than not announcing it at all.
 */

import { memo, useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, motion, radius, space } from '../../theme';
import { Text } from './Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface ComingSoonBadgeProps {
  label?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const ComingSoonBadge = memo(function ComingSoonBadge({
  label = 'Coming Soon',
  style,
  testID = 'coming-soon-badge',
}: ComingSoonBadgeProps) {
  const t = useT();
  const pulse = useRef(new Animated.Value(1)).current;
  // `null` until the platform has answered. Starting on an assumption and
  // correcting it a tick later means someone who asked for reduced motion
  // still gets a pulse, briefly — which is exactly what they asked not to
  // have.
  const [reduceMotion, setReduceMotion] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void AccessibilityInfo.isReduceMotionEnabled()
      .then((enabled) => {
        if (!cancelled) setReduceMotion(enabled);
      })
      .catch(() => {
        // Not every platform answers; a still badge is the safe default.
        if (!cancelled) setReduceMotion(true);
      });
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReduceMotion,
    );
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (reduceMotion !== false) {
      pulse.setValue(1);
      return;
    }
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 0.55,
          duration: motion.duration.slow,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 1,
          duration: motion.duration.slow,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [pulse, reduceMotion]);

  return (
    // The pill stays solid and only its words pulse: fading the whole pill
    // let the outline it sits across show through it.
    <View
      testID={testID}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      aria-hidden
      style={[styles.badge, style]}
    >
      <Animated.View style={{ opacity: pulse }}>
        <Text variant="badge" tone="onBrand">
          {t(label)}
        </Text>
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create({
  badge: {
    // Small, because it sits under a label inside a button rather than
    // standing on its own.
    paddingHorizontal: space.xs,
    // Not zero: Urdu sets taller than Latin at the same size, and with no
    // vertical padding the pill cropped its own text.
    paddingVertical: space.xxs,
    borderRadius: radius.full,
    backgroundColor: colors.bg.brand,
  },
});
